import * as React from 'react';
import { Box, ButtonBase, FormLabel, Grid, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ComputerIcon from '@mui/icons-material/Computer';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import GamepadIcon from '@mui/icons-material/Gamepad';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';

/**
 * Preferred-platforms tile grid.
 *
 * Renders each platform as a clickable tile with an icon on top and
 * a label beneath. Tiles are multi-select — clicking toggles the
 * platform in/out of the selected set. Selected tiles get a primary-
 * colored border, a filled background tint, and a small check
 * indicator in the top-right corner.
 *
 * The icons are MUI's neutral device / gamepad icons — we do not
 * ship the branded console marks, so this tile grid uses shape
 * cues (desktop, gamepad, retro cartridge, handheld) plus the
 * text label to identify each platform.
 */

export interface PlatformOption {
  readonly value: string;
  readonly label: string;
  readonly Icon: React.ComponentType<{ readonly fontSize?: 'small' | 'medium' | 'large' }>;
}

export const PLATFORM_OPTIONS: ReadonlyArray<PlatformOption> = [
  { value: 'pc', label: 'PC', Icon: ComputerIcon },
  { value: 'xbox', label: 'Xbox', Icon: SportsEsportsIcon },
  { value: 'playstation', label: 'PlayStation', Icon: GamepadIcon },
  { value: 'nintendoSwitch', label: 'Nintendo Switch', Icon: VideogameAssetIcon },
  { value: 'steamDeck', label: 'Steam Deck', Icon: DevicesOtherIcon },
];

export interface PlatformTileGridProps {
  readonly label: string;
  readonly value: ReadonlyArray<string>;
  readonly onChange: (next: ReadonlyArray<string>) => void;
}

/**
 * Controlled tile grid. `value` is the current set of selected
 * platform ids; `onChange` receives the next set on every toggle.
 */
export function PlatformTileGrid(props: PlatformTileGridProps): JSX.Element {
  const toggle = (v: string): void => {
    if (props.value.includes(v)) {
      props.onChange(props.value.filter((x) => x !== v));
    } else {
      props.onChange([...props.value, v]);
    }
  };
  return (
    <Box>
      <FormLabel component="legend" sx={{ mb: 1, display: 'block' }}>
        {props.label}
      </FormLabel>
      <Grid container spacing={1.5}>
        {PLATFORM_OPTIONS.map((option) => {
          const selected = props.value.includes(option.value);
          return (
            <Grid key={option.value} size={{ xs: 6, sm: 4 }}>
              <ButtonBase
                onClick={() => toggle(option.value)}
                aria-pressed={selected}
                aria-label={option.label}
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  border: 1,
                  borderColor: selected ? 'primary.main' : 'divider',
                  borderWidth: selected ? 2 : 1,
                  bgcolor: selected ? 'primary.light' : 'transparent',
                  color: selected ? 'primary.contrastText' : 'text.primary',
                  transition: 'all 120ms ease-in-out',
                  position: 'relative',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: selected ? 'primary.light' : 'action.hover',
                  },
                }}
              >
                <Stack
                  spacing={0.5}
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 2,
                    px: 1,
                    width: '100%',
                    minHeight: 96,
                  }}
                >
                  <option.Icon fontSize="large" />
                  <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }}>
                    {option.label}
                  </Typography>
                </Stack>
                {selected && (
                  <CheckCircleIcon
                    fontSize="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      color: 'primary.main',
                      bgcolor: 'background.paper',
                      borderRadius: '50%',
                    }}
                  />
                )}
              </ButtonBase>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
